import React, { useCallback, useEffect, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  createPaymentIntent,
  fetchPaymentConfig,
  type CreatePaymentIntentResponse,
} from '../../../features/payment/paymentApi';

const BREAKDOWN_KEYS_INLINE = new Set([
  'tax_quote_total_eur',
  'tax_quote_service_cost_eur',
  'tax_quote_iva_eur',
  'tax_quote_note',
]);

/** Figma: row 528×40, gap 32px; each control 248×40, py-8 px-16, radius 8, border 1px (Back). */
const PAYMENT_ACTIONS_ROW =
  'flex w-full max-w-[528px] flex-col gap-4 sm:flex-row sm:items-center sm:gap-8';
const PAYMENT_BTN_SECONDARY =
  'box-border inline-flex h-10 min-h-[40px] w-full shrink-0 items-center justify-center gap-2 ' +
  'rounded-lg border border-solid border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold ' +
  'text-[#131315] leading-none transition-colors hover:bg-[#F9FAFB] disabled:opacity-50 sm:w-[248px]';
const PAYMENT_BTN_PRIMARY =
  'box-border inline-flex h-10 min-h-[40px] w-full shrink-0 items-center justify-center gap-2 ' +
  'rounded-lg border border-transparent bg-[#07A2A0] px-4 py-2 text-sm font-semibold ' +
  'leading-none text-white transition-colors hover:bg-[#069694] disabled:cursor-not-allowed ' +
  'disabled:opacity-50 sm:w-[248px]';

interface CheckoutFormProps {
  amountDisplay: string;
  quoteBreakdown: Record<string, string>;
  showInlineFeeSummary: boolean;
  onSuccess: (paymentIntentId: string) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  amountDisplay,
  quoteBreakdown,
  showInlineFeeSummary,
  onSuccess,
  onBack,
  isSubmitting,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements || !elementReady) return;
      setPaying(true);
      setError(null);

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message ?? 'Payment failed. Please try again.');
        setPaying(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        setConfirmed(true);
        try {
          await onSuccess(paymentIntent.id);
        } catch {
          setConfirmed(false);
          setError('Payment confirmed but could not continue. Please try again.');
          setPaying(false);
        }
      } else {
        setError('Payment could not be confirmed. Please try again.');
        setPaying(false);
      }
    },
    [stripe, elements, onSuccess, elementReady]
  );

  if (confirmed) {
    return (
      <div className="flex items-center gap-3 text-gray-600 max-w-md">
        <div className="w-5 h-5 border-2 border-[#07A2A0] border-t-transparent rounded-full animate-spin shrink-0" />
        <span className="text-sm">Payment confirmed. Completing setup…</span>
      </div>
    );
  }

  const breakdownEntries = Object.entries(quoteBreakdown).filter(
    ([k]) => !BREAKDOWN_KEYS_INLINE.has(k)
  );

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      {showInlineFeeSummary ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Tax filing fee</span>
            <span className="text-2xl font-semibold text-gray-900 tabular-nums">{amountDisplay}</span>
          </div>
          {breakdownEntries.length > 0 && (
            <div className="border-t border-gray-200 pt-3 flex flex-col gap-1">
              {breakdownEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm text-gray-500">
                  <span className="capitalize">
                    {k.replace(/tax_quote_line_/g, '').replace(/_/g, ' ')}
                  </span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <PaymentElement onReady={() => setElementReady(true)} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className={PAYMENT_ACTIONS_ROW}>
        <button
          type="button"
          onClick={onBack}
          disabled={paying || isSubmitting}
          className={PAYMENT_BTN_SECONDARY}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || !elementReady || paying || isSubmitting}
          className={PAYMENT_BTN_PRIMARY}
        >
          {paying ? 'Processing…' : 'Pay'}
        </button>
      </div>
    </form>
  );
};

export interface StripePaymentBlockProps {
  sessionId: string;
  onNext: (patch: Record<string, unknown>) => Promise<void> | void;
  onBack: () => void;
  isSubmitting: boolean;
  /** When false, fee lines are shown outside this block (merged quote screen). */
  showInlineFeeSummary: boolean;
}

/**
 * Stripe Elements + PaymentElement for the flow; optionally shows the legacy inline fee card.
 */
export function StripePaymentBlock({
  sessionId,
  onNext,
  onBack,
  isSubmitting,
  showInlineFeeSummary,
}: StripePaymentBlockProps) {
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [intentData, setIntentData] = useState<CreatePaymentIntentResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);

    Promise.all([fetchPaymentConfig(), createPaymentIntent(sessionId)])
      .then(([config, intent]) => {
        if (cancelled) return;
        if (intent.client_secret !== 'pi_test_bypass_secret' && config.publishable_key) {
          loadStripe(config.publishable_key).then((s) => {
            if (!cancelled) setStripeInstance(s);
          });
        }
        setIntentData(intent);
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err?.message ?? 'Could not load payment details.');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleSuccess = useCallback(
    async (paymentIntentId: string): Promise<void> => {
      await onNext({ payment_intent_id: paymentIntentId });
    },
    [onNext]
  );

  if (loadError) {
    return (
      <div className="flex max-w-[528px] flex-col gap-4">
        <p className="text-red-600 text-sm">{loadError}</p>
        <button onClick={onBack} type="button" className={PAYMENT_BTN_SECONDARY}>
          Back
        </button>
      </div>
    );
  }

  if (intentData?.client_secret === 'pi_test_bypass_secret') {
    return (
      <div className="flex max-w-[528px] flex-col gap-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Payment is disabled in this environment. Click Continue to proceed.
        </div>
        <div className={PAYMENT_ACTIONS_ROW}>
          <button onClick={onBack} type="button" className={PAYMENT_BTN_SECONDARY}>
            Back
          </button>
          <button
            type="button"
            onClick={() => handleSuccess('pi_test_bypass')}
            disabled={isSubmitting}
            className={PAYMENT_BTN_PRIMARY}
          >
            Continue (dev bypass)
          </button>
        </div>
      </div>
    );
  }

  if (intentData?.already_paid) {
    const paidAmount = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: (intentData.currency ?? 'eur').toUpperCase(),
    }).format((intentData.amount_cents ?? 0) / 100);

    return (
      <div className="flex max-w-[528px] flex-col gap-6">
        <div className="rounded-xl border border-[#07A2A0] bg-[#F0FAFA] p-5 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#07A2A0] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-semibold text-[#07A2A0]">Payment received</span>
          </div>
          <p className="text-sm text-gray-600 pl-7">
            Your payment of <span className="font-semibold text-gray-900">{paidAmount}</span> has been confirmed.
          </p>
        </div>
        <div className={PAYMENT_ACTIONS_ROW}>
          <button onClick={onBack} type="button" className={PAYMENT_BTN_SECONDARY}>
            Back
          </button>
          <button
            type="button"
            onClick={() => handleSuccess(intentData.payment_intent_id)}
            disabled={isSubmitting}
            className={PAYMENT_BTN_PRIMARY}
          >
            {isSubmitting ? 'Continuing…' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  if (!intentData || !stripeInstance) {
    return (
      <div className="flex items-center gap-3 text-gray-500">
        <div className="w-5 h-5 border-2 border-[#07A2A0] border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading payment details…</span>
      </div>
    );
  }

  const amountDisplay = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format((intentData.amount_cents ?? 0) / 100);

  return (
    <Elements
      stripe={stripeInstance}
      options={{
        clientSecret: intentData.client_secret,
        appearance: {
          theme: 'stripe',
          variables: { colorPrimary: '#07A2A0' },
        },
      }}
    >
      <CheckoutForm
        amountDisplay={amountDisplay}
        quoteBreakdown={intentData.quote.breakdown}
        showInlineFeeSummary={showInlineFeeSummary}
        onSuccess={handleSuccess}
        onBack={onBack}
        isSubmitting={isSubmitting}
      />
    </Elements>
  );
}
